import { useEffect, useMemo, useState } from "react";
import { Apirequests } from "../../utils/ApiRequests";
import CustomOffCanvas from "../../components/CustomOffCanvas";
import AddOrEditWorkshop from "./AddOrEditWorkshop";
import { Edit2, Loader, Trash2 } from "lucide-react";
import { downloadExcel } from "../../utils/DownloadXls";
import { toast } from "sonner";

const WorkshopList = () => {
    const [workshops, setWorkshops] = useState([]);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [isOffCanvasOpen, setIsOffCanvasOpen] = useState({ state: false, name: "" });
    const [workshop, setWorkshop] = useState(null);
    const [deleting, setDeleting] = useState('');

    const itemsPerPage = 8;

    // =========================
    // Fetch Workshops
    // =========================
    const getWorkshops = async () => {
        try {
            setLoading(true);
            setError("");
            const token = localStorage.getItem("token");

            const resp = await Apirequests(`workshops`, "GET", {}, { Authorization: `Bearer ${token}`, });

            if (resp?.success) {
                setWorkshops(resp?.data || []);
            }
        } catch (error) {
            console.error(error);
            setError(error.response?.data?.message || "Failed to fetch workshops");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getWorkshops();
    }, []);

    // =========================
    // Search
    // =========================
    const filteredWorkshops = useMemo(() => {
        const value = search.toLowerCase().trim();
        if (!value) return workshops;

        return workshops.filter((workshop) => {
            return (
                workshop.title?.toLowerCase().includes(value) ||
                workshop.language?.toLowerCase().includes(value) ||
                workshop.status?.toLowerCase().includes(value)
            );
        });
    }, [workshops, search]);

    // =========================
    // Pagination
    // =========================
    const totalPages = Math.ceil(filteredWorkshops.length / itemsPerPage);

    const paginatedWorkshops = filteredWorkshops.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handleSearch = (e) => {
        setSearch(e.target.value);
        setCurrentPage(1);
    };

    const getImageUrl = (thumbnail) => {
        if (!thumbnail) return "";

        if (
            thumbnail.startsWith("http://") ||
            thumbnail.startsWith("https://")
        ) {
            return thumbnail;
        }

        return `${process.env.REACT_APP_IMAGE_URI}${thumbnail}`;
    };

    const formatDate = (date) => {
        if (!date) return "-";

        return new Date(date).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const formatPrice = (price) => {
        // works whether price is Number
        // or { amount, currency }
        const amount =
            typeof price === "object" ? price?.amount : price;

        if (amount === undefined || amount === null) return "-";

        return `₹${amount}`;
    };


    const handleDelete = async (id) => {

        const token = localStorage.getItem("token");

        setDeleting(id);

        try {
            const res = await Apirequests(`workshops/delete/${id}`, "DELETE", {}, {
                Authorization: `Bearer ${token}`,
            });
            if (res?.success) {
                toast.success(res?.message);
                await getWorkshops();
            } else {
                toast.error(res?.message)
            }
        } catch (error) {
            toast.error(error?.message)
        }
        setDeleting('')
    }

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
            <div className="mx-auto max-w-7xl">

                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-2xl font-semibold text-gray-900 sm:text-3xl">
                        Workshops
                    </h1>

                    <p className="mt-1 text-sm text-gray-500">
                        Manage and view all workshops
                    </p>
                </div>

                {/* Main Container */}
                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">

                    {/* Search */}
                    <div className="flex flex-col gap-4 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="relative w-full sm:max-w-md">
                            <svg
                                className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="m21 21-4.35-4.35m2.35-5.65a8 8 0 1 1-16 0 8 8 0 0 1 16 0Z"
                                />
                            </svg>

                            <input
                                type="text"
                                value={search}
                                onChange={handleSearch}
                                placeholder="Search workshops..."
                                className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-gray-500 focus:ring-2 focus:ring-gray-200"
                            />
                        </div>
                        <div className="flex gap-5">
                            <button onClick={() => setIsOffCanvasOpen({ state: true, name: "showworkshop" })} className="px-8 py-2 bg-yellow-500 text-black rounded-xl">+ Add</button>
                            <button onClick={() => downloadExcel(workshops, "workshops")} className="px-8 py-2 bg-indigo-500 text-white rounded-xl">⬇️ Download</button>
                        </div>

                        <div className="text-sm text-gray-500">
                            {filteredWorkshops.length} workshops
                        </div>
                    </div>

                    {/* Loading */}
                    {loading && (
                        <div className="flex min-h-64 items-center justify-center">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-gray-700" />
                        </div>
                    )}

                    {/* Error */}
                    {!loading && error && (
                        <div className="p-10 text-center">
                            <p className="text-sm text-red-500">
                                {error}
                            </p>

                            <button
                                onClick={getWorkshops}
                                className="mt-4 rounded-lg bg-gray-900 px-4 py-2 text-sm text-white"
                            >
                                Retry
                            </button>
                        </div>
                    )}

                    {/* Table */}
                    {!loading && !error && (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[900px] text-left">
                                    <thead className="bg-gray-50">
                                        <tr className="text-xs uppercase tracking-wide text-gray-500">
                                            <th className="px-5 py-4 font-medium">
                                                Workshop
                                            </th>

                                            <th className="px-5 py-4 font-medium">
                                                Start Date
                                            </th>

                                            <th className="px-5 py-4 font-medium">
                                                End Date
                                            </th>

                                            <th className="px-5 py-4 font-medium">
                                                Language
                                            </th>

                                            <th className="px-5 py-4 font-medium">
                                                Price
                                            </th>

                                            <th className="px-5 py-4 font-medium">
                                                Rating
                                            </th>

                                            <th className="px-5 py-4 font-medium">
                                                Status
                                            </th>
                                            <th className="px-5 py-4 font-medium">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody className="divide-y divide-gray-100">
                                        {paginatedWorkshops.map((workshop) => (
                                            <tr
                                                key={workshop._id}
                                                className="transition hover:bg-gray-50"
                                            >
                                                {/* Workshop */}
                                                <td className="px-5 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <img
                                                            src={getImageUrl(
                                                                workshop.thumbnail
                                                            )}
                                                            alt={workshop.title}
                                                            className="h-14 w-20 flex-shrink-0 rounded-lg object-cover"
                                                        />

                                                        <div className="max-w-[280px]">
                                                            <p className="font-medium text-gray-900">
                                                                {workshop.title}
                                                            </p>

                                                            <p className="mt-1 truncate text-xs text-gray-500">
                                                                {workshop.description}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>

                                                <td className="whitespace-nowrap px-5 py-4 text-sm text-gray-600">
                                                    {formatDate(workshop.startDate)}
                                                </td>

                                                <td className="whitespace-nowrap px-5 py-4 text-sm text-gray-600">
                                                    {formatDate(workshop.endDate)}
                                                </td>

                                                <td className="px-5 py-4 text-sm text-gray-600">
                                                    {workshop.language || "-"}
                                                </td>

                                                <td className="px-5 py-4 font-medium text-gray-900">
                                                    {formatPrice(workshop.price)}
                                                </td>

                                                <td className="px-5 py-4">
                                                    <div className="flex items-center gap-1 text-sm">
                                                        <span className="text-amber-500">
                                                            ★
                                                        </span>

                                                        <span className="font-medium text-gray-700">
                                                            {workshop.rating?.average || 0}
                                                        </span>

                                                        <span className="text-gray-400">
                                                            ({workshop.rating?.count || 0})
                                                        </span>
                                                    </div>
                                                </td>

                                                {/* Status */}
                                                <td className="px-5 py-4">
                                                    <span
                                                        className={`inline-flex rounded-full px-3 py-1 text-xs font-medium capitalize ${workshop.status === "active"
                                                            ? "bg-green-100 text-green-700"
                                                            : "bg-gray-100 text-gray-600"
                                                            }`}
                                                    >
                                                        {workshop.status}
                                                    </span>
                                                </td>

                                                <td className="px-5 py-4">
                                                    <div className="flex gap-5 items-center">
                                                        <Edit2
                                                            className="cursor-pointer text-indigo-500"
                                                            size={17}
                                                            onClick={() => { setIsOffCanvasOpen({ state: true, name: "editworkshop" }); setWorkshop(workshop) }}
                                                        />
                                                        {
                                                            deleting === workshop?._id ?
                                                                <Loader /> :
                                                                <Trash2
                                                                    className="cursor-pointer text-red-600" size={17}
                                                                    onClick={() => handleDelete(workshop?._id)}
                                                                />

                                                        }
                                                    </div>

                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Empty */}
                            {paginatedWorkshops.length === 0 && (
                                <div className="py-16 text-center">
                                    <p className="font-medium text-gray-700">
                                        No workshops found
                                    </p>

                                    <p className="mt-1 text-sm text-gray-400">
                                        Try changing your search.
                                    </p>
                                </div>
                            )}

                            {/* Pagination */}
                            {filteredWorkshops.length > 0 && (
                                <div className="flex flex-col gap-4 border-t border-gray-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                                    <p className="text-sm text-gray-500">
                                        Showing{" "}
                                        <span className="font-medium text-gray-700">
                                            {(currentPage - 1) *
                                                itemsPerPage +
                                                1}
                                        </span>{" "}
                                        to{" "}
                                        <span className="font-medium text-gray-700">
                                            {Math.min(
                                                currentPage * itemsPerPage,
                                                filteredWorkshops.length
                                            )}
                                        </span>{" "}
                                        of{" "}
                                        <span className="font-medium text-gray-700">
                                            {filteredWorkshops.length}
                                        </span>
                                    </p>

                                    <div className="flex items-center gap-1">
                                        <button
                                            disabled={currentPage === 1}
                                            onClick={() =>
                                                setCurrentPage((prev) =>
                                                    Math.max(prev - 1, 1)
                                                )
                                            }
                                            className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                                        >
                                            Previous
                                        </button>

                                        {Array.from(
                                            { length: totalPages },
                                            (_, index) => index + 1
                                        ).map((page) => (
                                            <button
                                                key={page}
                                                onClick={() =>
                                                    setCurrentPage(page)
                                                }
                                                className={`h-9 min-w-9 rounded-lg px-3 text-sm transition ${currentPage === page
                                                    ? "bg-gray-900 text-white"
                                                    : "text-gray-600 hover:bg-gray-100"
                                                    }`}
                                            >
                                                {page}
                                            </button>
                                        ))}

                                        <button
                                            disabled={
                                                currentPage === totalPages
                                            }
                                            onClick={() =>
                                                setCurrentPage((prev) =>
                                                    Math.min(
                                                        prev + 1,
                                                        totalPages
                                                    )
                                                )
                                            }
                                            className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            <CustomOffCanvas
                open={isOffCanvasOpen.state && isOffCanvasOpen.name === "showworkshop"}
                close={() => { setIsOffCanvasOpen({ state: false, name: "" }); setWorkshop(null) }}
                content={
                    <AddOrEditWorkshop workshop={workshop} close={() => { setIsOffCanvasOpen({ state: false, name: "" }); setWorkshop(null) }} refresh={getWorkshops} />
                }
            />

            <CustomOffCanvas
                open={isOffCanvasOpen.state && isOffCanvasOpen.name === "editworkshop"}
                close={() => { setIsOffCanvasOpen({ state: false, name: "" }); setWorkshop(null) }}
                content={
                    <AddOrEditWorkshop workshop={workshop} close={() => { setIsOffCanvasOpen({ state: false, name: "" }); setWorkshop(null) }} refresh={getWorkshops} />
                }
            />
        </div>
    );
};

export default WorkshopList;